/**
 * Optimized pagination component.
 *
 * Supports:
 * - Modern "Load more" pagination.
 * - Traditional pagination with infinite scroll enabled.
 * - Shopify Section Rendering requests.
 * - Near-viewport next-page prefetching.
 * - Shared response and in-flight request caches.
 * - AJAX quick-size initialization.
 */

window.collectionSectionCache ||=
  new Map();

window.collectionSectionPending ||=
  new Map();

if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();

      this.isInitialized = false;
      this.isFetching = false;
      this.controller = null;
      this.prefetchController = null;
      this.paginationObserver = null;
      this.prefetchObserver = null;
      this.fadeObserver = null;

      this.clickHandler =
        this.handleClick.bind(this);

      this.bfcacheHandler =
        this.reload.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;

      this.addEventListener(
        'click',
        this.clickHandler
      );

      document.addEventListener(
        'on:bfcache:load-restore',
        this.bfcacheHandler
      );

      this.init();
    }

    disconnectedCallback() {
      this.destroy();

      this.removeEventListener(
        'click',
        this.clickHandler
      );

      document.removeEventListener(
        'on:bfcache:load-restore',
        this.bfcacheHandler
      );

      this.isInitialized = false;
    }

    init() {
      this.infiniteScroll =
        this.dataset.infiniteScroll ===
        'true';

      this.pagination =
        this.querySelector(
          '.js-pagination'
        );

      this.loadMoreButton =
        this.querySelector(
          '.js-pagination-load-more'
        );

      this.results = [
        ...this.querySelectorAll(
          '.js-pagination-result'
        )
      ];

      this.isFetching = false;

      this.setupFadeObserver();
      this.setupPaginationBehavior();
    }

    destroy() {
      this.controller?.abort();
      this.prefetchController?.abort();

      this.controller = null;
      this.prefetchController = null;

      this.paginationObserver?.disconnect();
      this.prefetchObserver?.disconnect();
      this.fadeObserver?.disconnect();

      this.paginationObserver = null;
      this.prefetchObserver = null;
      this.fadeObserver = null;

      this.isFetching = false;
    }

    reload() {
      this.destroy();
      this.init();
    }

    setupPaginationBehavior() {
      if (
        !this.pagination ||
        !this.loadMoreButton ||
        this.pagination.dataset
          .isMoreResults !== 'true' ||
        this.results.length === 0
      ) {
        return;
      }

      if (this.infiniteScroll) {
        this.setupInfiniteScroll();
      } else {
        this.setupPrefetch();
      }
    }

    setupInfiniteScroll() {
      if (
        !('IntersectionObserver' in window) ||
        !this.pagination
      ) {
        return;
      }

      this.paginationObserver?.disconnect();

      this.paginationObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (
                entry.isIntersecting &&
                !this.isFetching
              ) {
                this.loadMore();
              }
            });
          },
          {
            root: null,
            rootMargin: '700px 0px',
            threshold: 0
          }
        );

      this.paginationObserver.observe(
        this.pagination
      );
    }

    setupPrefetch() {
      if (
        !('IntersectionObserver' in window) ||
        !this.loadMoreButton
      ) {
        return;
      }

      this.prefetchObserver?.disconnect();

      this.prefetchObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              this.prefetchObserver
                ?.unobserve(entry.target);

              this.prefetchNextPage(
                entry.target.href
              );
            });
          },
          {
            root: null,
            rootMargin: '700px 0px',
            threshold: 0
          }
        );

      this.prefetchObserver.observe(
        this.loadMoreButton
      );
    }

    setupFadeObserver() {
      if (
        !('IntersectionObserver' in window) ||
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
      ) {
        return;
      }

      this.fadeObserver?.disconnect();

      this.fadeObserver =
        new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              entry.target.classList.add(
                'is-visible'
              );

              observer.unobserve(
                entry.target
              );
            });
          },
          {
            root: null,
            rootMargin:
              '0px 0px -20px 0px',
            threshold: 0
          }
        );
    }

    handleClick(event) {
      const loadMoreButton =
        event.target.closest(
          '.js-pagination-load-more'
        );

      if (
        loadMoreButton &&
        this.contains(loadMoreButton)
      ) {
        event.preventDefault();
        this.loadMore();
        return;
      }

      const resultLink =
        event.target.closest(
          '.js-pagination-result a[href]'
        );

      if (
        resultLink &&
        this.contains(resultLink)
      ) {
        this.controller?.abort();
      }
    }

    getSectionId() {
      return (
        this.dataset.sectionId ||
        document
          .getElementById(
            'filter-results'
          )
          ?.dataset.productsSectionId ||
        ''
      );
    }

    getRequestDetails(url) {
      const pageUrl = new URL(
        url,
        window.location.origin
      );

      pageUrl.searchParams.delete(
        'section_id'
      );

      const requestUrl = new URL(
        pageUrl.toString()
      );

      const sectionId =
        this.getSectionId();

      if (sectionId) {
        requestUrl.searchParams.set(
          'section_id',
          sectionId
        );
      }

      return {
        pageUrl,
        requestUrl,
        cacheKey:
          requestUrl.toString()
      };
    }

    async fetchPage(url, signal) {
      const details =
        this.getRequestDetails(url);

      const cachedHTML =
        window.collectionSectionCache.get(
          details.cacheKey
        );

      if (cachedHTML) {
        return {
          ...details,
          html: cachedHTML,
          fromCache: true
        };
      }

      let pendingRequest =
        window.collectionSectionPending.get(
          details.cacheKey
        );

      if (!pendingRequest) {
        pendingRequest = fetch(
          details.requestUrl.toString(),
          {
            method: 'GET',
            headers: {
              'X-Requested-With':
                'XMLHttpRequest'
            },
            signal
          }
        ).then(async (response) => {
          if (!response.ok) {
            throw new Error(
              `Pagination request failed: ${response.status}`
            );
          }

          return response.text();
        });

        window.collectionSectionPending.set(
          details.cacheKey,
          pendingRequest
        );
      }

      try {
        const html =
          await pendingRequest;

        if (
          window.collectionSectionCache
            .size >= 30
        ) {
          const oldestKey =
            window.collectionSectionCache
              .keys()
              .next().value;

          window.collectionSectionCache.delete(
            oldestKey
          );
        }

        window.collectionSectionCache.set(
          details.cacheKey,
          html
        );

        return {
          ...details,
          html,
          fromCache: false
        };
      } catch (error) {
        window.collectionSectionCache.delete(
          details.cacheKey
        );

        throw error;
      } finally {
        if (
          window.collectionSectionPending.get(
            details.cacheKey
          ) === pendingRequest
        ) {
          window.collectionSectionPending.delete(
            details.cacheKey
          );
        }
      }
    }

    prefetchNextPage(url) {
      if (!url) return;

      this.prefetchController?.abort();

      this.prefetchController =
        new AbortController();

      this.fetchPage(
        url,
        this.prefetchController.signal
      ).catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn(
            '[CustomPagination prefetch]',
            error
          );
        }
      });
    }

    getResultKey(result) {
      return (
        result.dataset.productId ||
        result
          .querySelector(
            '[data-product-id]'
          )
          ?.dataset.productId ||
        ''
      );
    }

    appendResults(newResults) {
      const lastResult =
        this.results[
          this.results.length - 1
        ];

      if (!lastResult) {
        throw new Error(
          'No existing pagination result found.'
        );
      }

      const existingKeys = new Set(
        this.results
          .map((result) =>
            this.getResultKey(result)
          )
          .filter(Boolean)
      );

      const appendedItems = [];

      const fragment =
        document.createDocumentFragment();

      newResults.forEach(
        (result, index) => {
          const resultKey =
            this.getResultKey(result);

          if (
            resultKey &&
            existingKeys.has(resultKey)
          ) {
            return;
          }

          if (resultKey) {
            existingKeys.add(resultKey);
          }

          result.classList.add(
            'pagination-fade-in'
          );

          result.style.transitionDelay =
            `${Math.min(index, 8) * 40}ms`;

          appendedItems.push(result);
          fragment.appendChild(result);
        }
      );

      lastResult.after(fragment);

      this.results = [
        ...this.querySelectorAll(
          '.js-pagination-result'
        )
      ];

      appendedItems.forEach((item) => {
        if (this.fadeObserver) {
          this.fadeObserver.observe(item);
        } else {
          item.classList.add(
            'is-visible'
          );
        }

        if (
          typeof window.initQuickSizeAreas ===
          'function'
        ) {
          window.initQuickSizeAreas(item);
        }
      });

      return appendedItems;
    }

    updatePagination(newPagination) {
      this.paginationObserver?.disconnect();
      this.prefetchObserver?.disconnect();

      if (
        newPagination &&
        newPagination.dataset
          .isMoreResults === 'true'
      ) {
        this.pagination.innerHTML =
          newPagination.innerHTML;

        this.loadMoreButton =
          this.pagination.querySelector(
            '.js-pagination-load-more'
          );

        this.setupPaginationBehavior();
        return;
      }

      this.pagination?.remove();
      this.pagination = null;
      this.loadMoreButton = null;

      document
        .querySelectorAll(
          '.js-when-paginated-only'
        )
        .forEach((element) => {
          element.remove();
        });
    }
    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    // async loadMore() {
    //   if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
    //     this.isFetching = true;
    //     this.controller = new AbortController();

    //     try {
    //       // Add the loading state to the button
    //       if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

    //       this.loadMoreButton.classList.add('is-loading');
    //       this.loadMoreButton.setAttribute('aria-disabled', 'true');

    //       if (this.pagination.dataset.paginationStyle === 'traditional') {
    //         this.pagination.classList.add('is-loading');
    //       }

    //       // Retrieve and parse the next page
    //       const response = await fetch(this.loadMoreButton.href, {
    //         signal: this.controller.signal
    //       });

    //       if (!response.ok) throw new Error(response.status);
    //       if (this.dataset.pauseInfiniteScroll === 'true') {
    //         throw new Error('Infinite scroll was interrupted.');
    //       }

    //       const tmpl = document.createElement('template');
    //       tmpl.innerHTML = await response.text();

    //       // Append the additional results
    //       let newResultsHtml = '';
    //       const lastResult = this.results[this.results.length - 1];
    //       tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
    //         newResultsHtml += result.outerHTML;
    //       });
    //       lastResult.insertAdjacentHTML('afterend', newResultsHtml);
    //       this.results = this.querySelectorAll('.js-pagination-result');
    //       // 创建 IntersectionObserver 观察每个新元素
    //       const fadeObserver = new IntersectionObserver(
    //         (entries, observer) => {
    //           entries.forEach(entry => {
    //             if (entry.isIntersecting) {
    //               entry.target.classList.add('is-visible');
    //               observer.unobserve(entry.target); // 只动画一次
    //             }
    //           });
    //         },
    //         { rootMargin: '0px 0px -20px 0px' } // 提前 20px 开始动画，可调整
    //       );

    //       // 插入新元素
    //       const tempContainer = document.createElement('div');
    //       tempContainer.innerHTML = newResultsHtml;
    //       const newItems = Array.from(tempContainer.children);

    //       let lastItem = this.results[this.results.length - 1];

    //       newItems.forEach(item => {
    //         item.classList.add('pagination-fade-in');
    //         lastItem.insertAdjacentElement('afterend', item);
    //         lastItem = item;

    //         fadeObserver.observe(item); // 开始观察
    //       });

    //       this.results = this.querySelectorAll('.js-pagination-result');

    //       // Update the pagination content
    //       const newPagination = tmpl.content.querySelector('.js-pagination');
    //       if (newPagination.dataset.isMoreResults) {
    //         this.pagination.innerHTML = newPagination.innerHTML;

    //         // Rebind to the new load more button
    //         if (this.loadMoreButton) {
    //           this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
    //           this.loadMoreButton = this.querySelector('.js-pagination-load-more');
    //           this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
    //         }
    //       } else {
    //         this.pagination.remove();
    //         if (this.intersectingTimer) clearInterval(this.intersectingTimer);
    //         if (this.paginationObserver) this.paginationObserver.disconnect();
    //         document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
    //       }
    //     } catch (error) {
    //       console.log('Unable to fetch next page of results', error); // eslint-disable-line
    //       if (this.intersectingTimer) clearInterval(this.intersectingTimer);
    //     } finally {
    //       this.controller = null;

    //       // Remove the loading state from the load more button
    //       if (this.loadMoreButton) {
    //         this.loadMoreButton.classList.remove('is-loading');
    //         this.loadMoreButton.removeAttribute('aria-disabled');
    //       }

    //       this.isFetching = false;

    //       if (this.pagination.dataset.paginationStyle === 'traditional') {
    //         this.pagination.classList.remove('is-loading');
    //       }
    //     }
    //   }
    // }
    async loadMore() {
      if (
        this.isFetching ||
        this.dataset.pauseInfiniteScroll ===
          'true' ||
        !this.loadMoreButton ||
        !this.pagination
      ) {
        return;
      }

      this.isFetching = true;

      const activeButton =
        this.loadMoreButton;

      this.controller?.abort();

      this.controller =
        new AbortController();

      activeButton.classList.add(
        'is-loading'
      );

      activeButton.setAttribute(
        'aria-disabled',
        'true'
      );

      if (
        this.pagination.dataset
          .paginationStyle ===
        'traditional'
      ) {
        this.pagination.classList.add(
          'is-loading'
        );
      }

      try {
        const responseData =
          await this.fetchPage(
            activeButton.href,
            this.controller.signal
          );

        if (
          this.dataset.pauseInfiniteScroll ===
          'true'
        ) {
          throw new Error(
            'Pagination was interrupted.'
          );
        }

        const template =
          document.createElement(
            'template'
          );

        template.innerHTML =
          responseData.html;

        const newResults = [
          ...template.content
            .querySelectorAll(
              '.js-pagination-result'
            )
        ];

        const newPagination =
          template.content.querySelector(
            '.js-pagination'
          );

        if (newResults.length === 0) {
          window.collectionSectionCache.delete(
            responseData.cacheKey
          );

          throw new Error(
            'No pagination results found in response.'
          );
        }

        const appendedItems =
          this.appendResults(
            newResults
          );

        this.updatePagination(
          newPagination
        );

        document.dispatchEvent(
          new CustomEvent(
            'custom-pagination:appended',
            {
              bubbles: true,
              detail: {
                items: appendedItems,
                pageUrl:
                  responseData.pageUrl
                    .toString(),
                fromCache:
                  responseData.fromCache
              }
            }
          )
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn(
            '[CustomPagination]',
            error
          );
        }
      } finally {
        this.controller = null;
        this.isFetching = false;

        activeButton.classList.remove(
          'is-loading'
        );

        activeButton.removeAttribute(
          'aria-disabled'
        );

        this.pagination?.classList.remove(
          'is-loading'
        );

        /*
         * 新分页仍在视口内时，
         * 重新触发无限滚动检查。
         */
        if (
          this.infiniteScroll &&
          this.pagination &&
          this.pagination.dataset
            .isMoreResults === 'true'
        ) {
          this.paginationObserver?.disconnect();

          window.requestAnimationFrame(
            () => {
              if (
                this.isConnected &&
                !this.isFetching
              ) {
                this.setupInfiniteScroll();
              }
            }
          );
        }
      }
    }
  }

  customElements.define(
    'custom-pagination',
    CustomPagination
  );
}